import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  subWeeks,
  subMonths,
  subYears,
} from "date-fns";
import { useState, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import ReactDatePicker, { ReactDatePickerCustomHeaderProps } from "react-datepicker";
import { Box, Button, Divider, IconButton, TextField, Typography } from "@mui/material";

import {
  DateFilterArrowLeftIcon,
  DateFilterArrowRightIcon,
  DateFilterIcon,
} from "../../assets/icons/MUI/UIComponentsIcons";

const RangeDatePicker = ({
  startDate,
  endDate,
  onChange,
  placeholder,
}: {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (dates: [Date | null, Date | null]) => void;
  placeholder?: string;
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState<[Date | null, Date | null]>([startDate, endDate]);

  const handleDateRange = (value: string) => {
    const now = new Date();
    let newStartDate: Date | null = null;
    let newEndDate: Date | null = null;

    switch (value) {
      case "today":
        newStartDate = startOfDay(now);
        newEndDate = endOfDay(now);
        break;
      case "yesterday":
        newStartDate = startOfDay(subDays(now, 1));
        newEndDate = endOfDay(subDays(now, 1));
        break;
      case "thisWeek":
        newStartDate = startOfWeek(now, { weekStartsOn: 1 }); // Start week on Monday
        newEndDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case "lastWeek":
        newStartDate = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        newEndDate = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        break;
      case "thisMonth":
        newStartDate = startOfMonth(now);
        newEndDate = endOfMonth(now);
        break;
      case "lastMonth":
        newStartDate = startOfMonth(subMonths(now, 1));
        newEndDate = endOfMonth(subMonths(now, 1));
        break;
      case "thisYear":
        newStartDate = startOfYear(now);
        newEndDate = endOfYear(now);
        break;
      case "lastYear":
        newStartDate = startOfYear(subYears(now, 1));
        newEndDate = endOfYear(subYears(now, 1));
        break;
      case "allTime":
        // You might want to customize this based on your needs
        newStartDate = new Date(2020, 0, 1); // Example: Start from 2020
        newEndDate = endOfDay(now);
        break;
      default:
        return;
    }

    setTempDateRange([newStartDate, newEndDate]);
  };

  const CustomInput = forwardRef<HTMLInputElement, { value?: string; onClick?: () => void }>(
    ({ value, onClick }, ref) => (
      <TextField
        fullWidth
        inputRef={ref}
        value={value}
        onClick={onClick}
        placeholder={placeholder ?? t("components.rangeDatePicker.selectDate")}
        InputProps={{
          readOnly: true,
          startAdornment: <DateFilterIcon />,
        }}
        sx={styles.datePickerInput}
      />
    )
  );

  const CustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    isArabic = false,
  }: {
    isArabic: boolean;
  } & ReactDatePickerCustomHeaderProps) => (
    <Box sx={styles.datePickerHeader}>
      <IconButton onClick={decreaseMonth} disabled={prevMonthButtonDisabled} size="small">
        {isArabic ? <DateFilterArrowRightIcon /> : <DateFilterArrowLeftIcon />}
      </IconButton>
      <Typography sx={styles.datePickerHeaderTitle}>{format(date, "MMMM yyyy")}</Typography>
      <IconButton onClick={increaseMonth} disabled={nextMonthButtonDisabled} size="small">
        {isArabic ? <DateFilterArrowLeftIcon /> : <DateFilterArrowRightIcon />}
      </IconButton>
    </Box>
  );

  const handleApply = () => {
    onChange(tempDateRange);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempDateRange([startDate, endDate]);
    setIsOpen(false);
  };

  const customRangeOptions = [
    {
      label: t("components.rangeDatePicker.today"),
      value: "today",
    },
    {
      label: t("components.rangeDatePicker.yesterday"),
      value: "yesterday",
    },
    {
      label: t("components.rangeDatePicker.thisWeek"),
      value: "thisWeek",
    },
    {
      label: t("components.rangeDatePicker.lastWeek"),
      value: "lastWeek",
    },
    {
      label: t("components.rangeDatePicker.thisMonth"),
      value: "thisMonth",
    },
    {
      label: t("components.rangeDatePicker.lastMonth"),
      value: "lastMonth",
    },
    {
      label: t("components.rangeDatePicker.thisYear"),
      value: "thisYear",
    },
    {
      label: t("components.rangeDatePicker.lastYear"),
      value: "lastYear",
    },
    {
      label: t("components.rangeDatePicker.allTime"),
      value: "allTime",
    },
  ];

  return (
    <Box sx={styles.datePickerContainer}>
      <ReactDatePicker
        selectsRange
        startDate={tempDateRange[0]}
        endDate={tempDateRange[1]}
        onChange={(update: [Date | null, Date | null]) => setTempDateRange(update)}
        customInput={<CustomInput />}
        open={isOpen}
        onClickOutside={() => setIsOpen(false)}
        onInputClick={() => setIsOpen(true)}
        renderCustomHeader={(props) => <CustomHeader {...props} isArabic={i18n.language === "ar"} />}
        showPopperArrow={false}
        calendarContainer={({ children }) => (
          <Box sx={styles.calendarDatepickerContainer}>
            <Box sx={styles.datePickerCustomRangeContainer}>
              {customRangeOptions.map((option) => (
                <Button
                  variant="text-sm"
                  sx={{ textWrap: "nowrap" }}
                  key={option.value}
                  onClick={() => handleDateRange(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </Box>
            <Divider flexItem orientation="vertical" />
            <Box sx={styles.datePickerMainCalendarContainer}>
              {children}
              <Divider flexItem orientation="horizontal" />
              <Box sx={styles.datePickerFooter}>
                <Button variant="outlined-sm" onClick={handleCancel}>
                  {t("components.rangeDatePicker.cancel")}
                </Button>
                <Button variant="primary-sm" onClick={handleApply}>
                  {t("components.rangeDatePicker.apply")}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      />
    </Box>
  );
};

export default RangeDatePicker;

const styles = {
  datePickerContainer: { position: "relative" },
  calendarDatepickerContainer: {
    display: "flex",
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #E4E7EC)",
    background: "var(--Base-White, #FFF)",
    boxShadow: "0 20px 24px -4px rgba(16, 24, 40, 0.08), 0 8px 8px -4px rgba(16, 24, 40, 0.03)",
    "& .react-datepicker": {
      border: "none",
    },
    "& .react-datepicker__header": {
      backgroundColor: "transparent",
      borderBottom: "none",
      padding: 0,
    },
    "& .react-datepicker__day-name": {
      margin: "4px",
      width: "32px",

      color: "var(--Gray-700, #344054)",
      fontFamily: "Montserrat",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "20px",
    },
    "& .react-datepicker__day": {
      margin: "4px",
      width: "32px",
      height: "32px",
      lineHeight: "32px",
      borderRadius: "50% !important",
      color: "var(--Gray-700, #344054)",
      fontFamily: "Montserrat",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 500,

      "&:hover": {
        background: "var(--Gray-50, #F9FAFB)",
      },
      "&--selected": {
        background: "var(--Brand-600, #008B07) !important",
        color: "var(--Base-White, #FFF) !important",
      },
      "&--today": {
        background: "var(--Gray-100, #F2F4F7) !important",
      },
      "&--keyboard-selected": {
        background: "var(--Brand-600, #008B07) !important",
        color: "var(--Base-White, #FFF) !important",
      },
    },
    "& .react-datepicker__day--in-range": {
      background: "var(--Brand-600, #008B07) !important",
      color: "var(--Base-White, #FFF) !important",
    },
    "& .react-datepicker__day--outside-month": {
      visibility: "hidden",
    },
    "& .react-datepicker__month-container": {
      flex: 1,
    },
  },
  datePickerInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      border: "1px solid var(--Gray-300, #D0D5DD)",
      boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
      gap: "8px",
      color: "var(--Gray-700, #344054)",
      fontFamily: "Montserrat",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "20px",
    },
  },
  datePickerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
    px: "8px",
  },
  datePickerHeaderTitle: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  datePickerFooter: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    px: "8px",
  },
  datePickerMainCalendarContainer: { display: "flex", flexDirection: "column", gap: "12px", padding: "20px 0" },
  datePickerCustomRangeContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 8px",
  },
};
